import { AsYouType } from 'libphonenumber-js';
import Immutable from 'immutable';
import { getIntlMessage, getPublicFileUrl } from 'helpers/utilHelpers';

// Function returns user image or user image placeholder if image is empty
export const getUserAvatar = (image) => (image ? getPublicFileUrl(image) : null);

// Function get user initials
export const getInitials = (user) => {
  if (user) {
    let first = '',
      second = '';
    if (Immutable.isMap(user)) {
      first = user.get('firstname') ? user.get('firstname').charAt(0) : '';
      second = user.get('lastname') ? user.get('lastname').charAt(0) : '';
    } else {
      if (user.fullName && !user.firstname && !user.lastname) {
        const initials = user.fullName.split(' ');
        user.firstname = initials[0] ? initials[0] : '';
        user.lastname = initials[1] ? initials[1] : '';
      }
      first = user.firstname ? user.firstname.charAt(0) : '';
      second = user.lastname ? user.lastname.charAt(0) : '';
    }

    return first.toUpperCase() + second.toUpperCase();
  }
};

// Function get user phone in string format
export const getUserPhone = (phone) => {
  let data = phone;
  if (Immutable.isImmutable(phone)) {
    data = phone.toJS();
  }

  if (data?.countryCode && data?.number && data?.country) {
    const asYouType = new AsYouType(data.country);
    return asYouType.input(`+${data.countryCode} ${data.number}`);
  }

  return null;
};

// Function returns user full name
export const getUserFullName = (user, inviteMessage) => {
  if (!user) {
    return '';
  }

  let userObject = user ?? {};
  if (Immutable.isMap(user)) {
    userObject = user.toJS();
  }

  const { email, firstname, lastname, patronym, profileType: clientType } = userObject;
  const fullName = [lastname?.trim(), firstname?.trim(), patronym?.trim()].join(' ');

  if (fullName.trim()) {
    return fullName;
  }

  if (email) {
    const message =
      inviteMessage ?? getIntlMessage(clientType === 'contact' ? 'client.role.contact' : 'user.invited.email');
    return `${message}: ${email}`;
  }

  return getIntlMessage('user.invited');
};

// Function returns abbreviated name
export const getUserAbbreviatedName = (user) => {
  let name = '';
  if (!user) {
    return '';
  }

  if (user.get('lastname')) {
    name += user.get('lastname');
  }
  if (user.get('firstname')) {
    name += ` ${user.get('firstname')[0]}.`;
  }

  return name;
};

// Function returns user short name
export const getUserShortName = (user, secondInitial = false, lastnameFirst = false, isMe = false) => {
  if (!user) {
    return '';
  }

  let userObject = user ?? {};
  if (Immutable.isMap(user)) {
    userObject = user.toJS();
  }

  const { firstname, lastname, status, email, profileType: clientType } = userObject;
  if (!firstname && !lastname) {
    if (status === 'active') {
      return getIntlMessage('user.empty.name');
    }
    if (email) {
      const message = getIntlMessage(clientType === 'contact' ? 'client.role.contact' : 'user.invited.email');
      return `${message}: ${email}`;
    }

    return getIntlMessage('user.invited');
  }

  const me = isMe ? ` (${getIntlMessage('user.current')})` : '';

  if (!lastname) {
    return firstname + me;
  }

  if (secondInitial) {
    return lastnameFirst ? `${lastname} ${firstname[0]}.${me}` : `${firstname} ${lastname[0]}.${me}`;
  }

  return lastnameFirst ? `${lastname} ${firstname}${me}` : `${firstname} ${lastname}${me}`;
};

// Function returns concatenated user position and department if they set
export const getPositionAndDepartment = (user) => {
  let result = '';
  const position = user.get('positionName');
  const department = user.get('departmentName');

  const separator = position && department ? ' — ' : '';

  if (position) {
    result += position;
  }

  result += separator;

  if (department) {
    result += department;
  }

  return result;
};
